import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminApi, type QuestOptin } from '@/lib/adminApi';
import { Card, DataTable, Spinner, EmptyState, StatTile, type Column } from '@/components/ui';
import { IconAward, IconUsers } from '@/components/ui/icons';
import { AdminPage, AdminPageHeader, SearchBox } from '@/components/admin/shared';
import { Pager } from '@/components/admin/Pager';
import { Drawer, Field } from '@/components/admin/Drawer';
import { dateTime } from '@/lib/adminFormat';

const PAGE = 25;

/** Quest Opt-ins — the per-driver opt-in / progress log for the weekly driver
 *  quests. A driver creates a row by tapping "Opt In" on the Opportunities
 *  screen; incentives-svc then advances `trips_completed` off the trip stream.
 *  incentives-svc OWNS the `driver_quest_optins` table; admin-svc only reads it.
 *
 *  READ-ONLY here — by design. The legacy Filament resource was itself read-only
 *  (canCreate() = false): opt-ins are created by drivers, progress is machine-
 *  advanced, and incentives-svc exposes no admin create/edit/delete endpoint, so
 *  there is nothing for admin-svc to proxy a write to (and admin-svc never writes
 *  a table another service owns). This is a clean list + detail VIEW (no dead
 *  buttons). The section is DEFENSIVE (shows a "not yet available" state when the
 *  table isn't in this Postgres). Covers the legacy DriverQuestOptinResource (its
 *  Driver / Quest / Trips-done columns + the per-quest filter). */
export function QuestOptins() {
  const [q, setQ] = useState('');
  const [questId, setQuestId] = useState<number | ''>('');
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState<QuestOptin | null>(null);

  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: ['admin', 'quest-optins', q, questId, offset],
    queryFn: ({ signal }) =>
      adminApi.questOptins({ q, quest_id: questId === '' ? undefined : questId, limit: PAGE, offset }, signal),
    placeholderData: (prev) => prev,
  });

  // Stats roll-up (count / drivers / quests / trips) — its own light query so the
  // tiles render even while the table page is refetching.
  const stats = useQuery({
    queryKey: ['admin', 'quest-optins', 'stats'],
    queryFn: ({ signal }) => adminApi.questOptinStats(signal),
  });

  const unavailable = data && data.available === false;
  const questFilters = data?.quest_filters ?? [];

  const columns: Array<Column<QuestOptin>> = [
    { key: 'id', header: 'ID', cell: (r) => <span className="metric text-xs text-muted">#{r.id}</span> },
    {
      key: 'driver',
      header: 'Driver',
      cell: (r) => <span className="font-medium text-slate-100">{r.driver_label}</span>,
    },
    {
      key: 'quest',
      header: 'Quest',
      cell: (r) => <span className="text-slate-300">{r.quest_label}</span>,
    },
    {
      key: 'trips',
      header: 'Trips done',
      align: 'right',
      cell: (r) => <span className="metric text-sm text-brand-400">{r.trips_completed}</span>,
    },
    {
      key: 'opted',
      header: 'Opted in',
      cell: (r) => <span className="text-xs text-muted">{dateTime(r.opted_in_at)}</span>,
    },
    {
      key: 'view',
      header: '',
      align: 'right',
      cell: (r) => (
        <button
          type="button"
          onClick={() => setSelected(r)}
          className="rounded-md border border-ink-700 px-2 py-1 text-xs text-brand-400 hover:bg-ink-800"
        >
          View
        </button>
      ),
    },
  ];

  return (
    <AdminPage>
      <AdminPageHeader
        title="Quest Opt-ins"
        subtitle="Per-driver opt-ins + progress for the weekly driver quests."
        right={
          <SearchBox
            value={q}
            onChange={(v) => {
              setQ(v);
              setOffset(0);
            }}
            placeholder="Search by driver or quest…"
          />
        }
      />

      {unavailable ? (
        <Card>
          <EmptyState
            title="Quest opt-ins not yet in Postgres"
            description={
              data?.note ??
              'The driver_quest_optins table is owned by incentives-svc and is not in this Postgres yet.'
            }
            icon={<IconAward className="h-6 w-6" />}
          />
        </Card>
      ) : (
        <>
          {/* Stat tiles — glance figures over the opt-in set. */}
          <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <StatTile
              label="Total opt-ins"
              value={stats.data?.stats?.total ?? '—'}
              icon={<IconAward className="h-4 w-4" />}
            />
            <StatTile
              label="Drivers"
              value={stats.data?.stats?.drivers ?? '—'}
              tone="info"
              icon={<IconUsers className="h-4 w-4" />}
              hint="who've opted in"
            />
            <StatTile label="Quests" value={stats.data?.stats?.quests ?? '—'} hint="with opt-ins" />
            <StatTile
              label="Trips completed"
              value={stats.data?.stats?.trips_completed ?? '—'}
              tone="ok"
              hint="across all opt-ins"
            />
          </div>

          {/* Read-only notice — opt-ins are driver-created + machine-advanced; no
              authoring endpoint exists upstream. Keeps the page honest: no dead buttons. */}
          <div className="mb-3 flex items-start gap-2 rounded-lg border border-ink-800 bg-ink-850 px-3 py-2 text-xs text-text-secondary">
            <IconAward className="mt-0.5 h-4 w-4 shrink-0 text-brand-400" />
            <span>
              Read-only view. Drivers create opt-ins by tapping &ldquo;Opt In&rdquo; on the Opportunities screen, and
              progress is advanced automatically from the trip stream by incentives-svc.
            </span>
          </div>

          {/* Quest filter — mirrors the legacy Filament SelectFilter on quest_id.
              Only quests that actually have opt-ins appear, so it never lists an
              empty option. Hidden until the list reports at least one. */}
          {questFilters.length > 0 ? (
            <div className="mb-3 flex items-center gap-2">
              <label className="text-xs text-text-secondary">Quest</label>
              <select
                value={questId}
                onChange={(e) => {
                  setQuestId(e.target.value === '' ? '' : Number(e.target.value));
                  setOffset(0);
                }}
                className="input h-8 w-auto py-0 text-xs"
              >
                <option value="">All quests</option>
                {questFilters.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.title || `Quest #${f.id}`}
                  </option>
                ))}
              </select>
            </div>
          ) : null}

          <Card flush>
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Spinner label="Loading opt-ins…" />
              </div>
            ) : isError ? (
              <EmptyState title="Couldn't load opt-ins" description={(error as Error)?.message} />
            ) : (
              <>
                <DataTable<QuestOptin>
                  columns={columns}
                  rows={data?.items ?? []}
                  rowKey={(r) => String(r.id)}
                  stickyHeader
                  empty={
                    <EmptyState
                      title="No opt-ins"
                      description={
                        q || questId !== ''
                          ? 'No quest opt-ins match this filter.'
                          : 'No drivers have opted into a quest yet.'
                      }
                      icon={<IconAward className="h-6 w-6" />}
                    />
                  }
                />
                <Pager total={data?.total ?? 0} offset={offset} pageSize={PAGE} onOffset={setOffset} fetching={isFetching} />
              </>
            )}
          </Card>
        </>
      )}

      {selected !== null ? <QuestOptinDrawer optin={selected} onClose={() => setSelected(null)} /> : null}
    </AdminPage>
  );
}

// ─── opt-in detail drawer (read-only) ────────────────────────

/** QuestOptinDrawer — the read-only detail panel for one opt-in. Seeded directly
 *  from the row (the list already carries the full row), so it opens instantly.
 *  Purely informational, matching the read-only surface. */
function QuestOptinDrawer({ optin, onClose }: { optin: QuestOptin; onClose: () => void }) {
  return (
    <Drawer title={`Opt-in #${optin.id}`} subtitle={optin.quest_label} onClose={onClose}>
      <div className="space-y-4">
        <div className="flex items-baseline gap-3">
          <span className="metric text-2xl font-semibold text-brand-400">{optin.trips_completed}</span>
          <span className="text-sm text-text-secondary">trips completed</span>
        </div>

        <Card>
          <div>
            <Field label="ID" value={`#${optin.id}`} />
            <Field label="Driver" value={optin.driver_label} />
            <Field label="Quest" value={optin.quest_label} />
            <Field label="Trips done" value={optin.trips_completed} />
            <Field label="Opted in" value={dateTime(optin.opted_in_at)} />
            <Field label="Created" value={dateTime(optin.created_at)} />
          </div>
        </Card>

        <p className="text-[11px] text-text-muted">
          Read-only. This opt-in was created by the driver in-app; progress is advanced automatically by incentives-svc
          from the trip stream.
        </p>
      </div>
    </Drawer>
  );
}
